import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { Phone, Envelope, MapPin } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const contactReasons = [
  { id: "etre_appele", label: "Être appelé" },
  { id: "demande_rdv", label: "Demande de rendez-vous" },
  { id: "demande_devis", label: "Demande de devis" },
  { id: "renseignements", label: "Avoir des renseignements" }
];

export default function Contact() {
  const [formData, setFormData] = useState({
    prenom: "",
    nom: "",
    email: "",
    telephone: "",
    etablissement: "",
    message: "",
    reasons: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (reasonId, checked) => {
    setFormData(prev => ({
      ...prev,
      reasons: checked 
        ? [...prev.reasons, reasonId]
        : prev.reasons.filter(r => r !== reasonId)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.prenom || !formData.nom || !formData.email || !formData.telephone) {
      toast.error("Veuillez remplir tous les champs obligatoires.");
      return;
    }

    setIsSubmitting(true);

    try {
      // Format reasons as part of message
      const reasonsText = formData.reasons.length > 0 
        ? `\n\nMotif(s) de contact: ${formData.reasons.map(r => 
            contactReasons.find(cr => cr.id === r)?.label
          ).join(", ")}`
        : "";

      const response = await axios.post(`${API}/contact`, {
        prenom: formData.prenom,
        nom: formData.nom,
        email: formData.email,
        telephone: formData.telephone,
        etablissement: formData.etablissement,
        message: (formData.message || "Aucun message") + reasonsText
      });
      
      if (response.data.status === "success") {
        toast.success("Votre message a bien été envoyé. Nous vous répondrons sous 24 heures.");
        setFormData({
          prenom: "",
          nom: "",
          email: "",
          telephone: "",
          etablissement: "",
          message: "",
          reasons: []
        });
      }
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="contact-page">
      <Header />
      <main className="bg-slate-50">
        <section className="py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Left - Form */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-blue-900 mb-4">
                  Nous écrire
                </h1>
                <p className="text-sky-600 mb-8">
                  Vous souhaitez en savoir plus sur nos solutions, une demande de devis ou vous souhaitez être contacté par téléphone? remplissez ce formulaire, nous répondrons sous 24 heures.
                </p>

                <form 
                  onSubmit={handleSubmit} 
                  className="space-y-6"
                  data-testid="contact-form"
                >
                  {/* Name Fields */}
                  <div>
                    <Label className="text-sky-600 font-medium">
                      Nom <span className="text-red-500">*</span>
                    </Label>
                    <div className="grid grid-cols-2 gap-4 mt-2">
                      <div>
                        <Input
                          name="prenom"
                          value={formData.prenom}
                          onChange={handleChange}
                          className="border-slate-300 bg-white"
                          data-testid="input-prenom"
                          required
                        />
                        <span className="text-xs text-slate-500 mt-1">Prénom</span>
                      </div>
                      <div>
                        <Input
                          name="nom"
                          value={formData.nom}
                          onChange={handleChange}
                          className="border-slate-300 bg-white"
                          data-testid="input-nom"
                          required
                        />
                        <span className="text-xs text-slate-500 mt-1">Nom</span>
                      </div>
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <Label htmlFor="email" className="text-sky-600 font-medium">
                      E-mail <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="border-slate-300 bg-white mt-2"
                      data-testid="input-email"
                      required
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <Label htmlFor="telephone" className="text-sky-600 font-medium">
                      Téléphone <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="telephone"
                      name="telephone"
                      type="tel"
                      value={formData.telephone}
                      onChange={handleChange}
                      className="border-slate-300 bg-white mt-2"
                      data-testid="input-telephone"
                      required
                    />
                  </div>

                  {/* Establishment */}
                  <div>
                    <Label htmlFor="etablissement" className="text-sky-600 font-medium">
                      Établissement
                    </Label>
                    <Input
                      id="etablissement"
                      name="etablissement"
                      value={formData.etablissement}
                      onChange={handleChange}
                      className="border-slate-300 bg-white mt-2"
                      data-testid="input-etablissement"
                    />
                  </div>

                  {/* Checkboxes */}
                  <div className="space-y-3">
                    {contactReasons.map((reason) => (
                      <div key={reason.id} className="flex items-center space-x-3">
                        <Checkbox
                          id={reason.id}
                          checked={formData.reasons.includes(reason.id)}
                          onCheckedChange={(checked) => handleCheckboxChange(reason.id, checked)}
                          data-testid={`checkbox-${reason.id}`}
                        />
                        <Label 
                          htmlFor={reason.id} 
                          className="text-slate-700 cursor-pointer"
                        >
                          {reason.label}
                        </Label>
                      </div>
                    ))}
                  </div>

                  {/* Message */}
                  <div>
                    <Label htmlFor="message" className="text-slate-700 font-medium">
                      Commentaire ou message
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      className="border-slate-300 bg-white mt-2 resize-none"
                      data-testid="input-message"
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-blue-700 hover:bg-blue-800 text-white px-8 py-3 font-medium"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? "Envoi en cours..." : "Envoyer"}
                  </Button>
                </form>
              </motion.div>

              {/* Right - Contact Info & Map */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-8"
              >
                <div>
                  <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-blue-900 mb-6">
                    Où nous trouver
                  </h2>

                  <div className="space-y-4">
                    <div className="flex items-center gap-3 text-slate-700">
                      <Phone size={20} weight="fill" className="text-sky-500" />
                      <span>03 55 20 50 51</span>
                    </div>
                    <div className="flex items-center gap-3 text-slate-700">
                      <Envelope size={20} weight="fill" className="text-sky-500" />
                      <a href="mailto:contact@eatech.fr" className="hover:text-sky-600">
                        contact@eatech.fr
                      </a>
                    </div>
                    <div className="flex items-start gap-3 text-slate-700">
                      <MapPin size={20} weight="fill" className="text-sky-500 flex-shrink-0 mt-1" />
                      <div>
                        <p>11 rue d'Amsterdam</p>
                        <p>54500 VANDOEUVRE-LES-NANCY</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Google Maps Embed */}
                <div className="rounded-xl overflow-hidden shadow-lg border border-slate-200">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2635.7668372895097!2d6.165833076889961!3d48.65777591402892!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4794a27f7a4f5e3b%3A0x3e0a3e3e3e3e3e3e!2s11%20Rue%20d&#39;Amsterdam%2C%2054500%20Vand%C5%93uvre-l%C3%A8s-Nancy!5e0!3m2!1sfr!2sfr!4v1704067200000!5m2!1sfr!2sfr"
                    width="100%"
                    height="350"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="E@Tech Location"
                    data-testid="google-map"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
